import { MessageBundle } from '@amzn/arb-tools';
import { ChromeMenuItemConfig } from '@chrome/types';
import { MenuItemIds } from '@chrome/enums';

export interface AccountSideNavigationParams {
  intl: MessageBundle;
  entityId: string;
  globalAccountId: string;
  navigate: (url: string) => void;
  saEntityId?: string;
  saEntityUrl?: string;
  /**
   * True when the account has no Sponsored Ads account. Only known once account data has loaded,
   * so it defaults to false to keep the Sponsored Ads navigation until then.
   */
  isDspOnlyAccount?: boolean;
  /**
   * Manager Account owning the DSP entity and that entity's regional id. Both are required to
   * address DSP Manager Account pages: the page URL may carry the Global Account id, which the
   * DSP console cannot resolve.
   */
  managerAccountId?: string;
  dspEntityId?: string;
}

/** Builds a click handler that resolves its URL at click time, so bad data cannot break rendering. */
const assignOnClick = (getUrl: () => string) => () =>
  window.location.assign(getUrl());

/** The Sponsored Ads entity can live on another marketplace, reachable only on its own domain. */
const isSellingAccountOnOtherMarketplace = ({
  entityId,
  saEntityId,
  saEntityUrl,
}: AccountSideNavigationParams): boolean =>
  Boolean(saEntityId && saEntityUrl && saEntityId !== entityId);

const saEntityDomain = ({ saEntityUrl }: AccountSideNavigationParams): string =>
  new URL(saEntityUrl!).origin;

const dspManagerAccountUrl = ({
  managerAccountId,
  dspEntityId,
}: AccountSideNavigationParams): string =>
  `/am/managerAccounts/${managerAccountId}/dsp-advertisers/${dspEntityId}/accounts?entityId=${dspEntityId}`;

const managerAccountUrl = (params: AccountSideNavigationParams): string => {
  if (params.isDspOnlyAccount) {
    return dspManagerAccountUrl(params);
  }
  if (isSellingAccountOnOtherMarketplace(params)) {
    return `${saEntityDomain(params)}/advertisingAccounts/${params.saEntityId}/managerAccounts?entityId=${params.saEntityId}`;
  }
  return `/advertisingAccounts/${params.entityId}/managerAccounts`;
};

const sellingAccountUrl = (params: AccountSideNavigationParams): string => {
  if (isSellingAccountOnOtherMarketplace(params)) {
    return `${saEntityDomain(params)}/am/advertisingAccounts/${params.saEntityId}/retailPartners`;
  }
  return `/am/advertisingAccounts/${params.entityId}/retailPartners`;
};

const usersItem = ({
  intl,
  entityId,
}: AccountSideNavigationParams): ChromeMenuItemConfig => ({
  id: MenuItemIds.Users,
  label: intl.getMessage(MenuItemIds.Users),
  callback: entityId
    ? assignOnClick(() => `/user/management?${entityId}`)
    : undefined,
});

const accountDetailsItem = ({
  intl,
  globalAccountId,
  navigate,
}: AccountSideNavigationParams): ChromeMenuItemConfig => ({
  id: MenuItemIds.AccountDetails,
  label: intl.getMessage(MenuItemIds.AccountDetails),
  callback: globalAccountId
    ? () => navigate(`/ads-account/${globalAccountId}/account-settings`)
    : undefined,
  tag: 'new',
});

const managerAccountsItem = (
  params: AccountSideNavigationParams,
): ChromeMenuItemConfig => {
  // Without the Manager Account + DSP entity pair there is no reachable Manager Account page,
  // so the item is hidden rather than linking to a page that cannot load.
  const isUnreachable =
    Boolean(params.isDspOnlyAccount) &&
    !(params.managerAccountId && params.dspEntityId);

  return {
    id: MenuItemIds.ManagerAccount,
    label: params.intl.getMessage(MenuItemIds.ManagerAccount),
    callback:
      params.entityId && !isUnreachable
        ? assignOnClick(() => managerAccountUrl(params))
        : undefined,
    hide: isUnreachable,
  };
};

/**
 * Selling Account, Brands, Third Party Application and Sponsored ads deny list are all Sponsored
 * Ads features, so they have nothing to show for an account without a Sponsored Ads account.
 */
const sponsoredAdsOnlyItem = (
  { intl, entityId, isDspOnlyAccount }: AccountSideNavigationParams,
  id: MenuItemIds,
  getUrl: () => string,
): ChromeMenuItemConfig => ({
  id,
  label: intl.getMessage(id),
  callback: entityId ? assignOnClick(getUrl) : undefined,
  hide: Boolean(isDspOnlyAccount),
});

/**
 * Chrome side-navigation items for access and settings pages
 */
export const getAccountSideNavigationItems = (
  params: AccountSideNavigationParams,
): ChromeMenuItemConfig[] => [
  usersItem(params),
  accountDetailsItem(params),
  managerAccountsItem(params),
  sponsoredAdsOnlyItem(params, MenuItemIds.SellingAccount, () =>
    sellingAccountUrl(params),
  ),
  sponsoredAdsOnlyItem(params, MenuItemIds.Brands, () => '/brands'),
  sponsoredAdsOnlyItem(
    params,
    MenuItemIds.ThirdPartyApplication,
    () => '/am/thirdPartyApplications',
  ),
  sponsoredAdsOnlyItem(params, MenuItemIds.SponsoredAdsDenyList, () => '/denylist'),
];
